import type { EventRead, SpaceMemberStatus } from '@models/api';
import { getEventPhase, isEventEnded } from '@utils/eventLiveness';
import { eventSpaceIds } from '@utils/eventSpaceIds';
import { isEventCancelled } from '@utils/eventStatus';

/**
 * TECH-456: posting from an Event. A post "in" an Event is a post in one of the Event's host Spaces
 * by someone Going (the backend's event feed is built exactly that way), so the composer asks
 * "Are you going?" before it opens from any Event entry. This module is the pure decision; the
 * dialog and full-screen states live in `EventPostGate`, and `useEventPostGate` feeds it.
 */

/**
 * The Event surfaces that open the composer (useEventComposerEntry). The one list: it types the
 * `surface` they navigate with and is what `?surface=` is read against, so a surface added here
 * is reported under its own name instead of as a deep link.
 */
export const EVENT_COMPOSER_SURFACES = [
  'event_page_fab',
  'event_feed_empty',
  'for_you_invitation',
] as const;

export type EventComposerSurface = (typeof EVENT_COMPOSER_SURFACES)[number];

/** Where the composer was opened from. A `/create-question?event=` URL with no `surface` is a deep link. */
export type EventPostEntrySurface = EventComposerSurface | 'deep_link';

/** Reads `?surface=`; anything missing or unrecognised counts as a deep link. */
export const parseEventPostEntrySurface = (raw: string | null): EventPostEntrySurface =>
  EVENT_COMPOSER_SURFACES.find(surface => surface === raw) ?? 'deep_link';

/**
 * Going for posting purposes: an RSVP. Being the Event's creator no longer counts: creating an Event
 * writes the creator's Going row, and the creator of a calendar-synced Event is not its host and is
 * not counted as Going (backend TECH-319), so the Event feed would leave their post out.
 */
export const isGoingForPosting = (event: Pick<EventRead, 'is_going'>): boolean =>
  event.is_going === true;

/**
 * Whether an Event page shows its create-post (+). Only an Event with no host Space (eventSpaceIds:
 * the primary `space_id` plus every approved linked Space) hides it: there is nowhere for the post
 * to land. Everyone else gets (+), and the gate asks the question that fits the Event's timing
 * (Stuart, 2026-09-14: an ended Event asks "Were you there?" rather than hiding).
 */
export const showEventPostEntry = (
  event: Pick<EventRead, 'space_id' | 'linked_space_ids'>,
): boolean => eventSpaceIds(event).length > 0;

/** Which question the posting prompt asks. All three run the same Going (RSVP + host-Space enrolment). */
export type EventPostTense = 'upcoming' | 'live' | 'ended';

/**
 * `live` comes from the shared getEventPhase (the phase the Event cards label "I am here"). `ended`
 * comes from isEventEnded, the Event cards' own gate, which stays ended for good, unlike
 * getEventPhase's bounded recap window. Anything else, including the "soon" window and an Event
 * whose dates cannot be read, is upcoming: a malformed Event never asks "Were you there?".
 */
export const eventPostTense = (
  event: Pick<EventRead, 'start_date' | 'end_date'>,
  now: Date,
): EventPostTense => {
  if (getEventPhase(event, now) === 'live') return 'live';
  return isEventEnded(event, now) ? 'ended' : 'upcoming';
};

/** At or above this many people the attend prompt names the number; below it, "everyone". */
const CROWD_COUNT_THRESHOLD = 5;

const CROWD_PHRASE: Record<
  EventPostTense,
  { counted: (count: number) => string; everyone: string }
> = {
  upcoming: { counted: count => `the ${count} people going`, everyone: 'everyone going' },
  live: { counted: count => `the ${count} people here now`, everyone: 'everyone here now' },
  ended: {
    counted: count => `the ${count} people who were there`,
    everyone: 'everyone who was there',
  },
};

/**
 * The attend prompt's value line: where the post shows (this Event's feed) and who sees it. A small
 * crowd reads as "everyone" rather than a discouraging "2 people". It deliberately does not say
 * "you'll join {Space}": for an approval or PIN Space Going only sends a request, and the Event
 * payload can't tell which kind of Space it is before the tap. The attend toast confirms the join.
 */
export const attendPromptDescription = (
  tense: EventPostTense,
  crowdCount: number | null,
): string => {
  const phrase = CROWD_PHRASE[tense];
  const audience =
    crowdCount !== null && crowdCount >= CROWD_COUNT_THRESHOLD
      ? phrase.counted(crowdCount)
      : phrase.everyone;
  return `Your post shows on this event's feed, for ${audience}.`;
};

/**
 * What the gate knows about one host Space, read from its Space detail. `membershipStatus` is null
 * when the viewer has no standing there. A read refused as "not found or not allowed" keeps the last
 * standing the cache holds for that Space, and is null only when the cache holds none.
 */
export interface HostSpaceStanding {
  id: string;
  name: string;
  membershipStatus: SpaceMemberStatus | null;
}

/** A host Space the attend response left a join request with, as the server named it. */
export interface AttendPendingSpace {
  id: string;
  name: string;
}

export type EventPostPromptVariant = 'not_going' | 'join_spaces';

export type EventPostGateState =
  | { kind: 'loading' }
  | { kind: 'sign_in' }
  | { kind: 'event_unavailable' }
  | { kind: 'event_error' }
  | { kind: 'spaces_error' }
  | { kind: 'host_spaces_error' }
  | { kind: 'prompt'; variant: EventPostPromptVariant }
  /**
   * A cancelled Event takes no new Going, so for someone not Going there is no question to ask and
   * no feed to post to: the composer opens plain, with no prompt shown or tracked.
   */
  | { kind: 'event_cancelled' }
  | { kind: 'no_space' }
  /** A request is pending in a host Space the joined list does not name: see the resolver. */
  | { kind: 'waiting'; spaceName: string }
  | { kind: 'cant_post' }
  /** `memberHostIds`: the host Spaces the joined list names, joined or with a request pending. */
  | { kind: 'open'; memberHostIds: string[] };

export interface EventPostGateInput {
  /** Auth has not settled yet (neither a signed-in member nor a confirmed guest). */
  authSettling: boolean;
  isGuest: boolean;
  /**
   * The Event cannot be decided on yet: its read has not answered (no Event and no error, which
   * is also what a first fetch paused offline looks like), or a cached Event is still being
   * refetched for the gate's first decision.
   */
  eventLoading: boolean;
  /** The resolved Event; undefined once `eventLoading` is false means the read failed. */
  event:
    | (Pick<EventRead, 'space_id' | 'linked_space_ids' | 'is_going'> &
        Partial<Pick<EventRead, 'status'>>)
    | undefined;
  /**
   * The Event read failed for a reason other than "not found or not allowed": a request that failed
   * (a 5xx, a timeout, a dropped connection), worth a retry. False for a missing or hidden Event,
   * which no retry brings back.
   */
  eventError: boolean;
  /**
   * Joined Spaces cannot be decided on yet: never loaded, more pages to come, a cached list still
   * being refetched for the gate's first decision, or a retry after an error in flight.
   */
  membershipLoading: boolean;
  /** The joined-Spaces read failed and no retry is in flight. */
  membershipError: boolean;
  /**
   * Every page of the viewer's joined Spaces: the Spaces they can post into, a pending membership
   * included (a post there is held until a host approves). The only source of membership.
   */
  joinedSpaceIds: readonly string[];
  /**
   * A Going for this Event is in flight outside this gate's own prompt, or one has ended (here or
   * elsewhere) and the gate has not finished re-reading the Event and joined Spaces after it.
   */
  goingInFlight: boolean;
  /**
   * A Going succeeded on this screen: the server confirmed the RSVP (so it counts as Going before
   * the Event refetches) and host-Space enrolment has already been re-run. Never optimistic.
   */
  goingAttempted: boolean;
  /**
   * Host Spaces the Going on this screen left a request with (the attend response's `pending` and
   * `pin_required`, read through `requestedSpacesOf`).
   * Empty before a Going here, and when it left none.
   */
  attendPending: readonly AttendPendingSpace[];
  /**
   * A host Space's standing could not be read, for a reason other than "not found or not allowed":
   * a request that failed (a 5xx, a timeout, a dropped connection), with no retry of it in flight.
   * Consulted where `hostStandings` is.
   */
  hostStandingsError: boolean;
  /**
   * Each host Space's standing for the viewer. Undefined until every read has answered and none
   * is being refetched. Consulted only for someone Going whose joined Spaces name no host Space
   * and who has no `attendPending`.
   */
  hostStandings: readonly HostSpaceStanding[] | undefined;
}

/**
 * The gate's one decision, in priority order: auth, the Event, a Going in flight, host Spaces, then
 * Going, and membership last. "Are you going?" reads no membership, so it neither waits for the
 * joined Spaces to load nor is blocked by their error. Errors never fall through to the "not a
 * member" states, and an optimistic or in-flight Going never counts as membership.
 *
 * The gate opens for someone Going as soon as their joined Spaces name one host Space. That list
 * holds the Spaces where their request is still pending as well as the ones they are in, so a
 * member waiting on a host gets the composer, and the post is held until the host approves.
 *
 * Someone Going whose joined Spaces name no host Space gets, in order: Waiting, when the attend
 * response already named a host Space their request went to; the retry state, when any host
 * Space's standing could not be read; the loader, until every host Space's standing is known;
 * before a Going from this screen, Waiting when EVERY host Space holds a request of theirs (an
 * attend could change nothing) and otherwise Join (an attend may enrol them); after one, Waiting
 * when any host Space holds a request, otherwise Can't post here.
 *
 * So Waiting is a fallback, not what a pending member normally sees. It is reached only while the
 * joined list does not carry a request that exists: the list has not been read again since the
 * request was made, or it came from a backend that does not send pending rows.
 */
export const resolveEventPostGate = (input: EventPostGateInput): EventPostGateState => {
  if (input.authSettling) return { kind: 'loading' };
  if (input.isGuest) return { kind: 'sign_in' };
  if (input.eventLoading) return { kind: 'loading' };

  const { event } = input;
  if (!event) return input.eventError ? { kind: 'event_error' } : { kind: 'event_unavailable' };
  if (input.goingInFlight) return { kind: 'loading' };

  const hostIds = eventSpaceIds(event);
  if (hostIds.length === 0) return { kind: 'no_space' };

  const going = isGoingForPosting(event) || input.goingAttempted;
  if (!going) {
    return isEventCancelled(event)
      ? { kind: 'event_cancelled' }
      : { kind: 'prompt', variant: 'not_going' };
  }

  if (input.membershipError) return { kind: 'spaces_error' };
  if (input.membershipLoading) return { kind: 'loading' };

  const joined = new Set(input.joinedSpaceIds);
  const memberHostIds = hostIds.filter(spaceId => joined.has(spaceId));
  if (memberHostIds.length > 0) return { kind: 'open', memberHostIds };

  // From here on the joined list names no host Space, as a member or with a request pending. A
  // request can still exist that the list does not carry: the attend response and the host
  // Spaces' own details are what can say so.
  if (input.attendPending.length > 0) {
    return { kind: 'waiting', spaceName: input.attendPending[0].name };
  }

  // Ahead of every standing: one unread host Space could be the one that decides between Waiting,
  // Join and Can't post here, so a request known in another host Space does not stand in for it.
  if (input.hostStandingsError) return { kind: 'host_spaces_error' };
  if (!input.hostStandings) return { kind: 'loading' };
  const pending = input.hostStandings.filter(standing => standing.membershipStatus === 'pending');
  if (!input.goingAttempted) {
    return pending.length > 0 && pending.length === input.hostStandings.length
      ? { kind: 'waiting', spaceName: pending[0].name }
      : { kind: 'prompt', variant: 'join_spaces' };
  }
  return pending.length > 0
    ? { kind: 'waiting', spaceName: pending[0].name }
    : { kind: 'cant_post' };
};
